<script setup lang="ts">
import { defineAsyncComponent, ref, watch } from 'vue'

import { registerCreditMessages } from '../i18n'

import { useI18n, useLocaleStore } from '@/shared/i18n'
import { Spinner } from '@/shared/ui'

const TITLE_KEY = 'credits.planSelection.title'
const ComparePlansPage = defineAsyncComponent(() => import('./ComparePlansPage.vue'))
const localeStore = useLocaleStore()
const { t, te } = useI18n()
const messagesReady = ref(te(TITLE_KEY))

watch(
  () => localeStore.locale,
  async (locale) => {
    messagesReady.value = te(TITLE_KEY)
    if (messagesReady.value) return
    try {
      await registerCreditMessages(locale)
      messagesReady.value = true
    } catch {
      // Never expose raw keys while the feature locale chunk is unavailable.
    }
  },
  { immediate: true },
)
</script>

<template>
  <section
    class="plan-selection h-full overflow-auto py-lg px-[clamp(var(--ui-space-sm),_3vw,_var(--ui-space-2xl))] text-emphasis-ink bg-emphasis-canvas max-md:p-sm"
    data-testid="plan-selection-surface"
  >
    <div
      v-if="!messagesReady"
      class="plan-selection__loading grid min-h-80 place-items-center"
      aria-live="polite"
    >
      <Spinner>{{ t('common.loading') }}</Spinner>
    </div>
    <ComparePlansPage v-else />
  </section>
</template>

<style scoped>
.plan-selection .plan-selection__header > span {
  padding-block: var(--ui-space-2xs);
  padding-inline: var(--ui-space-sm);
  color: var(--ui-color-emphasis-vivid);
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid var(--ui-color-emphasis-vivid);
  border-radius: var(--ui-radius-md);
}
.plan-selection .plan-selection__header h1 {
  margin-top: var(--ui-space-md);
  margin-inline: 0;
  margin-bottom: 0;
  color: var(--ui-color-emphasis-ink);
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  line-height: 1.2;
}
.plan-selection .plan-selection__header p {
  margin-top: var(--ui-space-sm);
  margin-inline: 0;
  margin-bottom: 0;
  color: var(--ui-color-emphasis-muted);
  font-size: 1rem;
  font-weight: 500;
}
</style>
